import { StrictMode } from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import { flushSync } from 'react-dom';
import { HelmetProvider } from 'react-helmet-async';
import { initWebVitals } from '@drzach/website-toolkit';
import App, { preloadRouteModules } from './App.tsx';
import { captureLandingAttribution } from './lib/landing-attribution';
import './index.css';

// Pages are prerendered with React's renderToString (src/entry-server.tsx,
// scripts/prerender.js), so the served DOM carries the Suspense markers and
// text-node separators hydrateRoot needs and is ADOPTED rather than replaced.
// The earlier Playwright-snapshot prerender could never hydrate (B8 verdict,
// 2026-08-28: snapshots lack <!--$--> markers and merge adjacent text nodes,
// React #418 at every interpolation) — that is why the SSR path exists. The
// flag is the documented rollback point to the atomic remount below.
const HYDRATE = true;

// Routes whose prerendered markup is known to differ from the initial client
// render. These always take the remount path.
const HYDRATE_EXCLUDE = new Set<string>([]);

initWebVitals();
// Before any client-side navigation can drop the landing URL's tags.
captureLandingAttribution();

const container = document.getElementById('root')!;

const app = (
  <StrictMode>
    <HelmetProvider>
      <App />
    </HelmetProvider>
  </StrictMode>
);

function normalizedPath(): string {
  const p = window.location.pathname;
  return p.length > 1 && p.endsWith('/') ? p.slice(0, -1) : p;
}

async function boot() {
  const prerendered = container.hasChildNodes();

  // Resolve the current route's lazy module(s) BEFORE React touches the DOM:
  // hydration must not suspend (a suspended boundary with no server markers
  // discards the DOM and re-renders), and the remount below must be able to
  // commit real content in its single synchronous frame.
  let routeReady = false;
  if (prerendered) {
    try {
      routeReady = await preloadRouteModules(normalizedPath());
    } catch {
      routeReady = false;
    }
  }

  (window as Window & { __BOOT_INFO?: object }).__BOOT_INFO = {
    prerendered,
    routeReady,
    path: normalizedPath(),
  };
  if (HYDRATE && prerendered && routeReady && !HYDRATE_EXCLUDE.has(normalizedPath())) {
    hydrateRoot(container, app, {
      onRecoverableError: (error, errorInfo) => {
        // A mismatch React recovered from by client-rendering a subtree. The
        // prerender's hydration gate (scripts/prerender.js --verify) fails the
        // build on any of these, so in production this should never fire.
        console.warn(
          '[hydration-recoverable]',
          error,
          errorInfo?.componentStack?.split('\n').slice(0, 6).join(' << ') ?? ''
        );
      },
    });
    return;
  }

  // Atomic remount: the prerendered DOM (already painted via the inlined
  // critical CSS) is wiped and re-rendered in ONE task with a synchronous
  // first commit, so no intermediate frame — neither blank nor the Suspense
  // fallback — can paint between wipe and content. A scheduled (concurrent)
  // first render here paints the 100vh loader for a frame or two, which
  // scored ~0.95 CLS once critical CSS pulled first paint ahead of the
  // remount.
  const root = createRoot(container);
  if (prerendered) container.replaceChildren();
  flushSync(() => root.render(app));
}

// DEV-only style guide (docs/classic-look/BUILD-BRIEF.md, Lane F): every ui
// component plus the header and footer at /__styleguide. Never bundled into
// production (dead-code eliminated by the DEV check) and not an App route.
if (import.meta.env.DEV && window.location.pathname.replace(/\/$/, '') === '/__styleguide') {
  import('./dev/StyleGuide').then(({ default: StyleGuide }) => {
    createRoot(container).render(
      <StrictMode>
        <StyleGuide />
      </StrictMode>,
    );
  });
} else {
  void boot();
}
